import { createInertiaApp } from "@inertiajs/vue3";
import * as Sentry from "@sentry/vue";
import { createPinia } from "pinia";
import { createApp, h } from "vue";
import "../css/app.css";
import { createI18nPlugin } from "./i18n";
import { resolveInertiaPage } from "./pages";
import { initializeTheme } from "./shared/composables/useAppearance";
import {
  installAxiosCsrfRetry,
  installCsrfFetchRetry,
} from "./shared/csrfFetch";

const appName = import.meta.env.VITE_APP_NAME || "Laravel";
const sentryDsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const isSafari =
  typeof navigator !== "undefined" &&
  (navigator.vendor?.includes("Apple") ?? false);

installCsrfFetchRetry();
installAxiosCsrfRetry();

createInertiaApp({
  title: (title) => (title ? `${title} - ${appName}` : appName),
  resolve: resolveInertiaPage,
  setup({ el, App, props, plugin }) {
    const initialLocale = (props.initialPage?.props?.locale as string) || "en";
    const i18n = createI18nPlugin(initialLocale);

    const app = createApp({ render: () => h(App, props) })
      .use(plugin)
      .use(i18n)
      .use(createPinia());

    if (sentryDsn && !isSafari) {
      Sentry.init({
        app,
        dsn: sentryDsn,
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration({
            maskAllText: true,
            blockAllMedia: true,
          }),
        ],
        tracesSampleRate: 0.2,
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
        environment: import.meta.env.MODE,
      });
    }

    app.mount(el);
  },
  progress: {
    color: "#4B5563",
  },
});

// This will set light / dark mode on page load...
initializeTheme();
